import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  SmartToy as AIIcon,
  Assessment as ReportIcon,
  Map as MapIcon,
  History as HistoryIcon,
  BusinessCenter as CRMIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  LightbulbOutlined as TipIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UserGuidePage = () => {
  const navigate = useNavigate();

  const features = [
    { icon: <SearchIcon color="primary" />, title: 'Автоматический поиск', desc: 'Находите компании по названию города в обширной базе данных.' },
    { icon: <AIIcon color="secondary" />, title: 'Интеллектуальный анализ', desc: 'Используйте мощь искусственного интеллекта для глубокого анализа каждой компании.' },
    { icon: <ReportIcon color="success" />, title: 'Детальные отчеты', desc: 'Получайте подробную информацию, включая юридические данные, контакты и адреса.' },
    { icon: <MapIcon color="info" />, title: 'Интеграция с картами', desc: 'Визуализируйте местоположение компаний на интерактивной карте.' },
    { icon: <HistoryIcon color="warning" />, title: 'История поисков', desc: 'Сохраняйте результаты и возвращайтесь к ним в любое удобное время.' },
    { icon: <CRMIcon color="error" />, title: 'Экспорт в CRM', desc: 'Экспортируйте данные компаний в CRM с привязкой к менеджерам.' },
    { icon: <SpeedIcon sx={{ color: '#9c27b0' }} />, title: 'Высокая скорость', desc: 'Обрабатывайте десятки компаний одновременно, экономя ваше время.' },
  ];

  const faqSearchSettings = [
    {
      q: 'По какому принципу система ищет компании?',
      a: 'Поиск происходит по населенным пунктам (городам, поселкам). Система ищет организации на картах, соответствующие заданным критериям (розничные магазины инструментов, стройматериалов и т.д.). Поиск выделением произвольной области на карте не поддерживается.'
    },
    {
      q: 'Сколько компаний лучше указывать для поиска за один раз?',
      a: 'Рекомендуется запускать поиск пакетами. Оптимально — до 100 компаний (например, ставить задачу в конце рабочего дня, чтобы к утру она завершилась). Ориентировочное время обработки 20 компаний — около 40 минут.'
    },
    {
      q: 'Можно ли сменить город поиска?',
      a: 'Да, в интерфейсе доступен выпадающий список, где можно выбрать любой нужный город (например, переключиться с Нижнего Тагила на Екатеринбург).'
    },
    {
      q: 'Как сортируются результаты поиска?',
      a: 'Результаты выдаются от большего потенциала к меньшему. Вверху списка находятся наиболее релевантные клиенты (потенциал 85-100), внизу — менее подходящие.'
    }
  ];

  const faqDataQuality = [
    {
      q: 'Почему в результатах попадаются клиенты, с которыми мы уже работаем?',
      a: 'Система фильтрует клиентов, сверяя их ИНН с нашей базой. Однако, если название на карте (например, «Магазин Плитка») отличается от юридического названия (например, «ИП Иванов»), система может не распознать дубль. В будущих обновлениях появится поле «Юр. лицо» для упрощения ручной проверки.'
    },
    {
      q: 'Почему попадаются конкуренты, пункты выдачи маркетплейсов или непрофильные компании?',
      a: 'ИИ анализирует описание деятельности, но иногда может ошибаться. Мы формируем «Список исключений» для крупных сетей (ВсеИнструменты, Кувалда) и конкурентов-производителей, чтобы они не попадали в выдачу.'
    },
    {
      q: 'Почему иногда отображаются компании из других городов (не из того, который я выбрал)?',
      a: 'Ранее была проблема с подтягиванием юридических адресов вместо фактических. Сейчас алгоритм настроен на поиск именно фактических точек продаж в выбранном городе.'
    }
  ];

  const faqExport = [
    {
      q: 'Куда происходит экспорт: в 1С или в CRM?',
      a: 'Экспорт происходит в CRM. Кнопка может называться «Экспорт в 1С» (в старых версиях), но фактически данные уходят в систему продаж.'
    }
  ];

  const faqErrors = [
    {
      q: 'Процесс поиска завис (прогресс-бар не двигается) или время выполнения показывает странные цифры.',
      a: 'Попробуйте обновить страницу браузера (F5). Обычно это помогает восстановить отображение процесса. Если проблема сохраняется — сообщите разработчику.'
    },
    {
      q: 'При попытке экспорта возникает ошибка (API / Invalid token).',
      a: 'Обновите страницу и попробуйте нажать кнопку экспорта еще раз.'
    },
    {
      q: 'Я запустил новый поиск, но мне выдало те же самые компании, что и в прошлый раз.',
      a: 'Это техническая ошибка дублирования выборки. Она должна быть исправлена. Если вы снова столкнулись с этим — обязательно сообщите разработчикам.'
    }
  ];

  const timeEstimates = [
    { count: '1–10 компаний', time: 'от 2 до 20 минут' },
    { count: '10–50 компаний', time: 'от 20 минут до 2 часов' },
    { count: '50–100 компаний', time: 'от 2 до 4 часов' },
    { count: '100–200 компаний', time: 'от 4 часов и более' }
  ];

  const Section = ({ title, children, icon }) => (
    <Paper sx={{ p: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {icon}
        <Typography variant="h5" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );

  const FAQSection = ({ title, icon, items }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="h6" fontWeight="medium">
          {title}
        </Typography>
      </Box>
      {items.map((item, index) => (
        <Accordion key={index} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="medium">В: {item.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography color="text.secondary"><strong>О:</strong> {item.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Кнопка назад */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/')}
        >
          Вернуться к поиску
        </Button>
      </Box>

      {/* Заголовок */}
      <Paper 
        sx={{ 
          p: 5, 
          mb: 4, 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Руководство пользователя
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Система анализа компаний FIT-Audit
        </Typography>
      </Paper>

      {/* Введение */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="body1">
          Это руководство поможет вам освоить <strong>Систему анализа компаний</strong> — мощный инструмент 
          для автоматизированного поиска и интеллектуального анализа данных о компаниях на территории России.
        </Typography>
      </Paper>

      {/* Ключевые возможности */}
      <Section title="Ключевые возможности системы" icon={<SpeedIcon color="primary" fontSize="large" />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {features.map((feature, index) => (
            <Paper 
              key={index} 
              variant="outlined" 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main'
                }
              }}
            >
              <Box sx={{ mt: 0.5 }}>{feature.icon}</Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.desc}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Section>

      {/* Начало работы */}
      <Section title="1. Начало работы" icon={<CheckIcon color="success" fontSize="large" />}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Системные требования
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText 
              primary="Современный браузер" 
              secondary="Google Chrome, Mozilla Firefox, Safari или Microsoft Edge последних версий"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText 
              primary="Стабильное интернет-соединение" 
              secondary="Необходимо для быстрой загрузки и обработки данных"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText 
              primary="Разрешение экрана" 
              secondary="Рекомендуется не менее 1280x720 пикселей"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Получение учётной записи
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Для получения доступа к системе свяжитесь с администратором по электронной почте: <strong>akojevnikov@instrument-fit.ru</strong>
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Администратор создаст для вас учетную запись и предоставит данные для входа (Email и пароль).
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Процесс входа в систему
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="1. Откройте веб-браузер и перейдите по адресу системы" />
          </ListItem>
          <ListItem>
            <ListItemText primary="2. Нажмите кнопку «Войти» на главной странице" />
          </ListItem>
          <ListItem>
            <ListItemText primary="3. Введите ваш Email и пароль, полученные от администратора" />
          </ListItem>
          <ListItem>
            <ListItemText primary="4. Нажмите «Войти»" />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Для завершения сеанса работы воспользуйтесь кнопкой «Выйти» в правом верхнем углу экрана.
        </Typography>
      </Section>

      {/* Поиск и анализ */}
      <Section title="2. Поиск и анализ компаний" icon={<SearchIcon color="primary" fontSize="large" />}>
        <Typography variant="h6" gutterBottom>
          Создание нового поискового запроса
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText 
              primary="Название города" 
              secondary="Начните вводить название населенного пункта. Система поддерживает все города и населенные пункты России. Примеры: Екатеринбург, Москва, Санкт-Петербург"
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="Количество организаций" 
              secondary="Укажите, сколько компаний вы хотите проанализировать (от 1 до 200). Рекомендуется анализировать не менее 10 компаний за один раз."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Процесс анализа
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Поиск компаний" 
              secondary="Система обращается к государственным реестрам, Яндекс.Картам и другим открытым базам данных"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Контроль по ИНН" 
              secondary="Автоматическая проверка и исключение организаций, которые уже являются вашими клиентами или конкурентами"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Сбор информации" 
              secondary="Юридические и контактные данные, адреса, информация о веб-сайтах и социальных сетях"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><AIIcon color="secondary" /></ListItemIcon>
            <ListItemText 
              primary="Интеллектуальный анализ" 
              secondary="AI анализирует собранную информацию и формирует рекомендации"
            />
          </ListItem>
        </List>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Важно:</strong> не закрывайте и не перезагружайте страницу до завершения процесса анализа.
        </Alert>
      </Section>

      {/* Результаты */}
      <Section title="3. Просмотр результатов" icon={<ReportIcon color="success" fontSize="large" />}>
        <Typography variant="body1" paragraph>
          После завершения анализа открывается страница с результатами, где компании представлены в виде удобных карточек.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Информация в карточке компании:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Название компании — полное наименование организации" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="ИНН — идентификационный номер налогоплательщика" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Адрес — местоположение с возможностью перехода на Яндекс.Карты" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Телефоны — контактные номера для связи" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Веб-сайт — ссылка на официальный сайт компании" />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Внимание:</strong> Повторное попадание компаний в поисковую выдачу маловероятно, но все же возможно. 
          Просим вас быть внимательными при работе с результатами.
        </Alert>
      </Section>

      {/* Детальная информация */}
      <Section title="4. Детальная информация о компании" icon={<InfoIcon color="info" fontSize="large" />}>
        <Typography variant="body1" paragraph>
          При клике на карточку компании открывается детальная страница с полной информацией и AI-анализом.
        </Typography>

        <Typography variant="h6" gutterBottom>
          Вкладки информации:
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Основная информация</Typography>
            <Typography variant="body2" color="text.secondary">
              Полное название, ИНН, приоритет компании, оценка потенциала (0-100)
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Контакты</Typography>
            <Typography variant="body2" color="text.secondary">
              Телефоны, адреса email, мессенджеры и социальные сети
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Юридические данные</Typography>
            <Typography variant="body2" color="text.secondary">
              ОГРН, КПП, организационно-правовая форма, статус, дата регистрации
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Анализ клиента (AI)</Typography>
            <Typography variant="body2" color="text.secondary">
              Краткое описание, оценка потенциала, ключевые инсайты, рекомендации
            </Typography>
          </Paper>
        </Box>
      </Section>

      {/* История поисков */}
      <Section title="5. История поисков" icon={<HistoryIcon color="warning" fontSize="large" />}>
        <Typography variant="body1" paragraph>
          Все ваши поисковые запросы автоматически сохраняются и доступны в разделе «История поисков».
        </Typography>

        <Typography variant="h6" gutterBottom>
          В разделе истории вы можете:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary="Просматривать список всех выполненных поисков" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary="Видеть дату и время каждого запроса" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary="Узнать город и количество найденных компаний" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary="Получить уникальный ID задачи для обращения в техподдержку" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary="Вернуться к результатам любого прошлого поиска одним кликом" />
          </ListItem>
        </List>

        <Alert severity="success" sx={{ mt: 2 }}>
          Результаты хранятся неограниченное время и доступны только вам.
        </Alert>
      </Section>

      {/* Экспорт в CRM */}
      <Section title="6. Экспорт в CRM-систему" icon={<CRMIcon color="error" fontSize="large" />}>
        <Typography variant="body1" paragraph>
          Система позволяет экспортировать данные компаний в CRM с привязкой к конкретному менеджеру.
        </Typography>

        <Typography variant="h6" gutterBottom>
          Процесс экспорта:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="1. На странице компании нажмите кнопку «Экспорт в CRM»" />
          </ListItem>
          <ListItem>
            <ListItemText primary="2. В открывшемся окне выберите менеджера из списка (можно искать по имени или филиалу)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="3. Список менеджеров организован по филиалам и содержит ФИО каждого сотрудника" />
          </ListItem>
          <ListItem>
            <ListItemText primary="4. После выбора менеджера нажмите кнопку подтверждения" />
          </ListItem>
          <ListItem>
            <ListItemText primary="5. Данные компании будут экспортированы в CRM и привязаны к выбранному менеджеру" />
          </ListItem>
        </List>
      </Section>

      {/* Практические советы */}
      <Section title="7. Практические советы" icon={<TipIcon sx={{ color: '#ff9800' }} fontSize="large" />}>
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <strong>Важно:</strong> Анализ более 100 компаний может занять значительное время (4 часа и более). 
          Рекомендуется запускать такие задачи в конце рабочего дня, чтобы результаты были готовы к утру.
        </Alert>

        <List>
          <ListItem>
            <ListItemIcon><TipIcon sx={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText 
              primary="Начинайте с малого" 
              secondary="Если вы впервые используете систему, начните с анализа 5–10 компаний, чтобы оценить качество результатов."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><TipIcon sx={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText 
              primary="Оптимальный размер пакета — до 100 компаний" 
              secondary="Ориентировочное время обработки 20 компаний — около 40 минут."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><TipIcon sx={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText 
              primary="Уточняйте город" 
              secondary="Используйте полное название города (например, «Санкт-Петербург» вместо «СПб») для более точного поиска."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><TipIcon sx={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText 
              primary="Изучайте AI-анализ" 
              secondary="Не пренебрегайте информацией во вкладке «Анализ клиента». Рекомендации AI могут стать основой для принятия важных решений."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><TipIcon sx={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText 
              primary="Используйте оценку потенциала" 
              secondary="Обращайте внимание на числовую оценку потенциала компании — это поможет приоритизировать работу с клиентами."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Время обработки
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Количество компаний</strong></TableCell>
                <TableCell><strong>Примерное время</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeEstimates.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.count}</TableCell>
                  <TableCell>{row.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      {/* FAQ */}
      <Section title="8. Часто задаваемые вопросы (FAQ)" icon={<HelpIcon color="primary" fontSize="large" />}>
        <FAQSection 
          title="Поиск и настройки" 
          icon={<SettingsIcon color="primary" />} 
          items={faqSearchSettings} 
        />
        
        <Divider sx={{ my: 3 }} />
        
        <FAQSection 
          title="Качество данных и фильтрация" 
          icon={<BusinessIcon color="secondary" />} 
          items={faqDataQuality} 
        />
        
        <Divider sx={{ my: 3 }} />
        
        <FAQSection 
          title="Экспорт и работа с карточками" 
          icon={<UploadIcon color="success" />} 
          items={faqExport} 
        />
        
        <Divider sx={{ my: 3 }} />
        
        <FAQSection 
          title="Возможные ошибки и их решение" 
          icon={<BuildIcon color="error" />} 
          items={faqErrors} 
        />
      </Section>

      {/* Техподдержка */}
      <Paper 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <EmailIcon fontSize="large" />
          <Typography variant="h5" fontWeight="bold">
            9. Техническая поддержка
          </Typography>
        </Box>
        
        <Typography variant="body1" paragraph>
          Если у вас возникли вопросы или проблемы, свяжитесь с администратором системы:
        </Typography>
        
        <Chip 
          icon={<EmailIcon />} 
          label="akojevnikov@instrument-fit.ru" 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            color: 'white',
            fontSize: '1rem',
            py: 2.5,
            px: 1
          }} 
        />

        <Typography variant="body2" sx={{ mt: 3, opacity: 0.9 }}>
          При обращении укажите: ваш Email, дату и время проблемы, ID задачи (из истории поисков), описание ситуации и скриншоты.
        </Typography>
      </Paper>
    </Container>
  );
};

export default UserGuidePage;
